import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, makeStyles } from '@material-ui/core'
import { dataMarts } from '../../../../../../api/coreData'
import { useDenaliContext } from '../../../DenaliProvider'
import { decipherError } from '../../../../../../utils/decipherError'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import FlexRow from '../../../../../molecules/FlexRow'
import OperationalTable from '../../../../../organisms/OperationalTable'
import Loading from '../../../../../molecules/Loading'
import ErrorDisplay from '../../../../../molecules/ErrorDisplay'
import { useConfigColumns } from '../../../../../../hooks/useConfigColumns'
import { useFormattingContext } from '../../../../../organisms/FormattingProvider/FormattingContext'
import {
  getBatchStatus,
  getStatusColor,
  taskStatusFilters,
  batchStatusFilters
} from './batchStatusHelper'

const MIN_W = '0'
const BATCH_PAGE_SIZE = 20
const TASK_PAGE_SIZE = 50

const useStyles = makeStyles((theme) => ({
  section: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    padding: theme.layout.padding.p10,
    marginBottom: theme.layout.gap.g5
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: theme.layout.gap.g5
  },
  label: {
    fontWeight: 500,
    marginBottom: '4px',
    display: 'block'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#fff'
  },
  filterBar: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    marginBottom: '12px'
  },
  taskDetail: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    padding: theme.layout.padding.p10,
    marginTop: theme.layout.gap.g5
  },
  expandable: {
    background: 'var(--color-surface-secondary)',
    borderRadius: 'var(--border-radius-sm)',
    padding: theme.layout.padding.p10,
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    maxHeight: '200px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    marginTop: '4px'
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '8px',
    fontSize: '0.875rem'
  },
  runningIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.875rem',
    fontWeight: 500
  },
  taskTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8125rem'
  },
  taskTh: {
    textAlign: 'left',
    padding: '6px 8px',
    borderBottom: 'var(--border-neutral-thin)',
    fontWeight: 600
  },
  taskTd: {
    padding: '4px 8px',
    borderBottom: 'var(--border-neutral-thin)'
  },
  taskTdError: {
    padding: '4px 8px',
    borderBottom: 'var(--border-neutral-thin)',
    color: 'var(--color-danger-500)',
    fontWeight: 600
  },
  taskRowError: {
    backgroundColor: 'color-mix(in srgb, var(--color-danger-100) 30%, transparent)'
  },
  taskExpandedTd: {
    padding: '8px'
  },
  taskExpandedLabel: {
    marginBottom: '4px',
    fontWeight: 500,
    fontSize: '0.8125rem'
  },
  taskEmpty: {
    textAlign: 'center',
    padding: '16px',
    color: 'var(--color-text-secondary)'
  }
}))

function StatusBadge ({ status }) {
  const classes = useStyles()
  const { label, variant } = getBatchStatus(status)
  return (
    <span className={classes.statusBadge} style={{ backgroundColor: getStatusColor(variant) }}>
      {label}
    </span>
  )
}

StatusBadge.propTypes = { status: PropTypes.number }

function RunsTab ({ timeSeriesId, enabled }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const { setAlert } = useDenaliContext()
  const startMutation = dataMarts.useStartDataMart()

  const [batchPage, setBatchPage] = useState(0)
  const [batchStatusFilterKey, setBatchStatusFilterKey] = useState('all')
  const [selectedBatchId, setSelectedBatchId] = useState(null)
  const [taskPage, setTaskPage] = useState(0)
  const [taskStatusFilter, setTaskStatusFilter] = useState('all')
  const [expandedTaskId, setExpandedTaskId] = useState(null)
  const [asOfDate, setAsOfDate] = useState('')

  const batchFilters = useMemo(() => {
    const match = batchStatusFilters.find(f => f.key === batchStatusFilterKey)
    return match?.value ? { status: match.value } : {}
  }, [batchStatusFilterKey])

  const { data: latestBatchData } = dataMarts.useListDataMartBatches(
    timeSeriesId,
    { skip: 0, take: 1 }
  )

  const {
    data: batchData,
    isLoading: batchesLoading,
    error: batchesError,
    refetch: refetchBatches
  } = dataMarts.useListDataMartBatches(
    timeSeriesId,
    { skip: batchPage * BATCH_PAGE_SIZE, take: BATCH_PAGE_SIZE, filters: batchFilters }
  )

  const taskFilters = useMemo(() => {
    return taskStatusFilter !== 'all' ? { status: taskStatusFilter } : {}
  }, [taskStatusFilter])

  const {
    data: taskData,
    isLoading: tasksLoading,
    error: tasksError,
    refetch: refetchTasks
  } = dataMarts.useListDataMartBatchTasks(
    selectedBatchId,
    { skip: taskPage * TASK_PAGE_SIZE, take: TASK_PAGE_SIZE, filters: taskFilters, enabled: !!selectedBatchId }
  )

  const handleStart = useCallback(async () => {
    try {
      const command = { timeSeriesId }
      if (asOfDate) {
        command.asOfDate = asOfDate
      }
      await startMutation.mutateAsync(command)
      refetchBatches()
    } catch (err) {
      console.error('Failed to start data mart', err)
      setAlert({ severity: 'error', alertMessage: decipherError(err) })
    }
  }, [timeSeriesId, asOfDate, startMutation, refetchBatches, setAlert])

  const latestBatch = latestBatchData?.batches?.[0]
  const isCurrentlyRunning = latestBatch?.status === 0 || latestBatch?.status === 1

  const batchColumnConfig = useMemo(() => ({
    columns: [
      { id: 'batchId', accessor: 'batchId', Header: 'Batch ID' },
      {
        id: 'status',
        accessor: 'status',
        Header: 'Status',
        Cell: (cell) => <StatusBadge status={cell.value} />
      },
      {
        id: 'tasks',
        accessor: (row) => `${row.completedTasks || 0}/${row.totalTasks || 0}${row.failedTasks ? ` (${row.failedTasks} err)` : ''}`,
        Header: 'Tasks'
      },
      { id: 'lastStartedDate', accessor: 'lastStartedDate', Header: 'Started', format: 'timestamp' },
      { id: 'lastFinishedDate', accessor: 'lastFinishedDate', Header: 'Finished', format: 'timestamp' },
      { id: 'createdBy', accessor: 'createdBy', Header: 'Created By' },
      { id: 'createdDate', accessor: 'createdDate', Header: 'Created', format: 'timestamp' }
    ],
    defaultSort: [{ id: 'createdDate', desc: true }]
  }), [])

  const batchColumns = useConfigColumns({
    columnsConfig: batchColumnConfig,
    format: formatter
  })

  const batchTotalPages = Math.ceil((batchData?.total || 0) / BATCH_PAGE_SIZE)
  const taskTotalPages = Math.ceil((taskData?.total || 0) / TASK_PAGE_SIZE)

  return (
    <Grid container spacing={2}>
      {/* Start Section */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Start Data Mart</div>
          <Grid container spacing={2} alignItems='flex-end'>
            <Grid item xs={12} md={3}>
              <label className={classes.label}>As-of Date (optional)</label>
              <SydInput
                minWidth={MIN_W}
                type='date'
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SydButton
                variant='primary'
                size='sm'
                onClick={handleStart}
                disabled={startMutation.isLoading || !enabled || isCurrentlyRunning}
              >
                {startMutation.isLoading ? 'Starting...' : 'Start Data Mart'}
              </SydButton>
            </Grid>
            <Grid item xs={12} md={6}>
              {isCurrentlyRunning && (
                <div className={classes.runningIndicator}>
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor('info'),
                      display: 'inline-block'
                    }}
                  />
                  Batch #{latestBatch.batchId} is {latestBatch.status === 0 ? 'pending' : 'running'}
                </div>
              )}
              {!enabled && (
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  Data mart is disabled. Enable it in the Configuration tab to start.
                </span>
              )}
            </Grid>
          </Grid>
        </div>
      </Grid>

      {/* Batch History */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Batch History</div>
          <div className={classes.filterBar}>
            {batchStatusFilters.map(f => (
              <SydButton
                key={f.key}
                variant={batchStatusFilterKey === f.key ? 'primary' : 'outline'}
                size='sm'
                onClick={() => {
                  setBatchStatusFilterKey(f.key)
                  setBatchPage(0)
                }}
              >
                {f.label}
              </SydButton>
            ))}
          </div>
          {batchesError
            ? <ErrorDisplay error={batchesError} onRetry={refetchBatches} />
            : batchesLoading
              ? <Loading />
              : (
                <>
                  <OperationalTable
                    variant='v2'
                    itemName='Batches'
                    columns={batchColumns.columns}
                    defaultSort={batchColumns.defaultSort}
                    data={batchData?.batches || []}
                    onRowClick={row => {
                      setSelectedBatchId(row.original.batchId)
                      setTaskPage(0)
                      setTaskStatusFilter('all')
                      setExpandedTaskId(null)
                    }}
                  />
                  <div className={classes.pagination}>
                    <span>Page {batchPage + 1} of {Math.max(batchTotalPages, 1)} ({batchData?.total || 0} total)</span>
                    <FlexRow style={{ gap: '4px' }}>
                      <SydButton variant='outline' size='sm' onClick={() => setBatchPage(p => p - 1)} disabled={batchPage === 0}>
                        Prev
                      </SydButton>
                      <SydButton variant='outline' size='sm' onClick={() => setBatchPage(p => p + 1)} disabled={batchPage + 1 >= batchTotalPages}>
                        Next
                      </SydButton>
                    </FlexRow>
                  </div>
                </>
              )}
        </div>
      </Grid>

      {/* Task Detail */}
      {selectedBatchId && (
        <Grid item xs={12}>
          <div className={classes.section}>
            <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                Tasks for Batch #{selectedBatchId}
              </div>
              <SydButton variant='outline' size='sm' onClick={() => setSelectedBatchId(null)}>
                Close
              </SydButton>
            </FlexRow>
            <div className={classes.filterBar}>
              {taskStatusFilters.map(f => (
                <SydButton
                  key={f.value}
                  variant={taskStatusFilter === f.value ? 'primary' : 'outline'}
                  size='sm'
                  onClick={() => {
                    setTaskStatusFilter(f.value)
                    setTaskPage(0)
                  }}
                >
                  {f.label}
                </SydButton>
              ))}
            </div>
            {tasksError
              ? <ErrorDisplay error={tasksError} onRetry={refetchTasks} />
              : tasksLoading
                ? <Loading />
                : (
                  <>
                    <table className={classes.taskTable}>
                      <thead>
                        <tr>
                          {['Task ID', 'Type', 'Priority', 'Processing', 'Complete', 'Error', 'Retryable', 'Last Activity', ''].map(h => (
                            <th key={h} className={classes.taskTh}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(taskData?.tasks || []).map(task => (
                          <React.Fragment key={task.taskId}>
                            <tr className={task.isError ? classes.taskRowError : undefined}>
                              <td className={classes.taskTd}>{task.taskId}</td>
                              <td className={classes.taskTd}>{task.taskType}</td>
                              <td className={classes.taskTd}>{task.taskPriority}</td>
                              <td className={classes.taskTd}>{task.isProcessing ? 'Yes' : '-'}</td>
                              <td className={classes.taskTd}>{task.isComplete ? 'Yes' : '-'}</td>
                              <td className={task.isError ? classes.taskTdError : classes.taskTd}>
                                {task.isError ? 'Yes' : '-'}
                              </td>
                              <td className={classes.taskTd}>{task.isRetryable ? 'Yes' : '-'}</td>
                              <td className={classes.taskTd} style={{ fontSize: '0.75rem' }}>
                                {task.lastActivityDate ? formatter(task.lastActivityDate, 'timestamp') : '-'}
                              </td>
                              <td className={classes.taskTd}>
                                <SydButton
                                  variant='outline'
                                  size='sm'
                                  onClick={() => setExpandedTaskId(expandedTaskId === task.taskId ? null : task.taskId)}
                                >
                                  {expandedTaskId === task.taskId ? 'Hide' : 'Details'}
                                </SydButton>
                              </td>
                            </tr>
                            {expandedTaskId === task.taskId && (
                              <tr>
                                <td colSpan={9} className={classes.taskExpandedTd}>
                                  <div className={classes.taskExpandedLabel}>Task State</div>
                                  <div className={classes.expandable}>
                                    {JSON.stringify(task.taskState, null, 2)}
                                  </div>
                                  <div className={classes.taskExpandedLabel} style={{ marginTop: '8px' }}>Task Data</div>
                                  <div className={classes.expandable}>
                                    {JSON.stringify(task.taskData, null, 2)}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                        {(!taskData?.tasks || taskData.tasks.length === 0) && (
                          <tr>
                            <td colSpan={9} className={classes.taskEmpty}>
                              No tasks found for this filter.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className={classes.pagination}>
                      <span>Page {taskPage + 1} of {Math.max(taskTotalPages, 1)} ({taskData?.total || 0} total)</span>
                      <FlexRow style={{ gap: '4px' }}>
                        <SydButton variant='outline' size='sm' onClick={() => setTaskPage(p => p - 1)} disabled={taskPage === 0}>
                          Prev
                        </SydButton>
                        <SydButton variant='outline' size='sm' onClick={() => setTaskPage(p => p + 1)} disabled={taskPage + 1 >= taskTotalPages}>
                          Next
                        </SydButton>
                      </FlexRow>
                    </div>
                  </>
                )}
          </div>
        </Grid>
      )}
    </Grid>
  )
}

RunsTab.propTypes = {
  timeSeriesId: PropTypes.string.isRequired,
  enabled: PropTypes.bool
}

export default RunsTab
